import { useCallback, useMemo } from "react";
import type { BoardCollapsedChange } from "@design-system/Board/Board.types";
import type { BoardColumnFill } from "@components/views/board/boardColumnCollapse";
import type { BoardCollapsed } from "@components/views/board/boardLayoutCache";
import {
    NO_VIEW_KEY,
    type UpdateStoredBoardCollapse,
    useBoardColumnCollapse,
} from "@components/views/board/useBoardColumnCollapse";
import type { WorkBoardProps } from "./WorkBoard";

const NO_COLUMN_FILLS: BoardColumnFill[] = [];

type WorkBoardCells = Pick<
    WorkBoardProps,
    "rows" | "columns" | "getPlacements"
>;

/** Each column and whether no application is placed in it. */
const getWorkColumnFills = ({
    rows,
    columns,
    getPlacements,
}: WorkBoardCells): BoardColumnFill[] => {
    const filledColumnIds = new Set(
        rows.flatMap((row) =>
            getPlacements(row).map(({ columnId }) => columnId),
        ),
    );
    return columns.map(({ id }) => ({
        id,
        isEmpty: !filledColumnIds.has(id),
    }));
};

interface UseWorkBoardCollapseArgs {
    /** The picked Work view; null with none picked. */
    viewId: string | null;
    /** The stored collapse state; its columns are the ones collapsed by hand while they had cards. */
    collapsed: BoardCollapsed;
    updateCollapsed: (
        update: (collapsed: BoardCollapsed) => BoardCollapsed,
    ) => void;
    /** The columns and their applications; null while the board isn't showing them. */
    board: WorkBoardCells | null;
}

/** What the Work board collapses, and the handler that keeps it. */
export interface WorkBoardCollapseState extends BoardCollapsed {
    onCollapsedChange: (change: BoardCollapsedChange) => void;
}

/**
 * The Work board's collapsed columns and lanes. Empty columns collapse on
 * every view. Expanding an empty column holds for the session only, per
 * Work view. Toggling a column with cards, or a lane, is stored once per
 * organization and shared by every view.
 */
export const useWorkBoardCollapse = ({
    viewId,
    collapsed,
    updateCollapsed,
    board,
}: UseWorkBoardCollapseArgs): WorkBoardCollapseState => {
    const { collapsedColumnIds: handCollapsedIds, collapsedLaneIds } =
        collapsed;
    const columns = useMemo(
        () => (board === null ? NO_COLUMN_FILLS : getWorkColumnFills(board)),
        [board],
    );
    const updateStored = useCallback<UpdateStoredBoardCollapse>(
        (next) => updateCollapsed((latest) => next(latest) ?? latest),
        [updateCollapsed],
    );
    const { collapsedColumnIds, onCollapsedChange } = useBoardColumnCollapse({
        viewKey: viewId ?? NO_VIEW_KEY,
        columns,
        handCollapsedIds,
        updateStored,
    });

    return useMemo(
        () => ({ collapsedColumnIds, collapsedLaneIds, onCollapsedChange }),
        [collapsedColumnIds, collapsedLaneIds, onCollapsedChange],
    );
};
